import { addYears, differenceInMonths, differenceInYears } from 'date-fns';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'domains/dates/format';
import Logo from 'assets/expressable-logo.svg?react';
import { GoalSection } from 'components/GoalSection';
import { Heading, Paragraph } from 'components/Typography';
import { loadCarePlanFromStorage } from 'domains/carePlanPDF/data/localStorage';
import { CarePlanShortTermGoalSection } from 'pages/Dashboard/components/CarePlanShortTermGoalSection';
import { DATE_SHORT_HUMAN, DAY_MONTH_YEAR, TIME_WITH_ZONE_SHORT } from 'utils/date';

/**
 * NOTE(juan): This page is not meant to be accessed by users, instead this page is meant to be accessed by our external PDF service
 * That's why you'll see many hard-coded font values and no margins
 */

/**
 * NOTE(juan): In order to get the headless chromium to properly pick up page break CSS rules, the parent div can't be a flex
 * (it must be a block element), this and other limitations are present on this. Be very careful with any changes as its very unstable
 */

export const CarePlanPDF: React.FC = () => {
  const [summary] = useState(loadCarePlanFromStorage());

  if (!summary) {
    throw new Error('Tried to access care plan PDF page but no data was found');
  }

  const { clientInfo, goals, additionalGoals, therapistInfo, visitFrequency, diagnoses, medicalDiagnoses } = summary;
  const { t } = useTranslation();

  const clientDob = new Date(clientInfo.clientDateOfBirth);
  const clientAgeInYears = differenceInYears(new Date(), clientDob);
  const latestBirthday = addYears(clientDob, clientAgeInYears);
  const clientAgeInMonths = differenceInMonths(new Date(), latestBirthday);

  const fullTherapistName = `${therapistInfo.fullName}, ${therapistInfo.therapistCredentials}`;

  return (
    <div className="w-full">
      <header className="flex justify-between items-center mb-24">
        <Heading level="h2">{t(`pdfs.carePlan.title`)}</Heading>

        <Logo className="fill-current text-black-100% w-auto h-[22px]" title="Expressable" />
      </header>
      <Heading level="h6" className="mb-24">
        {t(`pdfs.carePlan.clientDetails.title`)}
      </Heading>
      <section className="grid grid-cols-3 gap-y-24 gap-x-12">
        <LabelValuePair label={t(`pdfs.carePlan.clientInfo.clientFirstName`)} value={clientInfo.clientFirstName} />
        <LabelValuePair label={t(`pdfs.carePlan.clientInfo.clientLastName`)} value={clientInfo.clientLastName} />
        <LabelValuePair
          label={t(`pdfs.carePlan.clientInfo.clientDateOfBirth`)}
          value={`${format(new Date(clientInfo.clientDateOfBirth), DATE_SHORT_HUMAN)} (${clientAgeInYears} ${t(
            `pdfs.carePlan.clientInfo.clientDateOfBirth.year${clientAgeInYears > 1 ? 's' : ''}`,
          )} ${clientAgeInMonths} ${t(
            `pdfs.carePlan.clientInfo.clientDateOfBirth.month${clientAgeInMonths > 1 ? 's' : ''}`,
          )})`}
        />
        <LabelValuePair label={t(`pdfs.carePlan.clientInfo.therapistName`)} value={fullTherapistName} />
        <LabelValuePair
          label={t(`pdfs.carePlan.clientInfo.frequency.title`)}
          value={t('pdfs.carePlan.clientInfo.frequency', {
            count: visitFrequency.sessionsCount,
            frequency: t(`pdfs.carePlan.clientInfo.frequency.period.${visitFrequency.frequency}`),
          })}
        />
        <LabelValuePair label={t(`pdfs.carePlan.clientInfo.reportDate`)} value={format(new Date(), DATE_SHORT_HUMAN)} />
      </section>
      <hr className="h-2 bg-black-200 w-full my-24" />

      <Heading level="h6" className="mb-24">
        {t(`pdfs.carePlan.diagnosis.title`)}
      </Heading>
      <section className="flex flex-col gap-24">
        {diagnoses.map(diagnosis => (
          <LabelValuePair label={diagnosis.diagnosisCode} value={diagnosis.description} key={diagnosis.description} />
        ))}

        {medicalDiagnoses.map(diagnosis => (
          <LabelValuePair label={diagnosis.code} value={diagnosis.description} key={diagnosis.description} />
        ))}
      </section>
      <hr className="h-2 bg-black-200 w-full my-24" />

      <Heading level="h6" className="mb-24">
        {t(`pdfs.carePlan.faqs.title`)}
      </Heading>
      <section className="flex flex-col gap-24">
        <LabelValuePair label={t('pdfs.carePlan.faqs.1.title')} value={t('pdfs.carePlan.faqs.1.description')} />
        <LabelValuePair label={t('pdfs.carePlan.faqs.2.title')} value={t('pdfs.carePlan.faqs.2.description')} />
        <LabelValuePair label={t('pdfs.carePlan.faqs.3.title')} value={t('pdfs.carePlan.faqs.3.description')} />
      </section>

      <Heading level="h6" className="mb-24 break-before-page">
        {t(`pdfs.carePlan.goals.title`)}
      </Heading>
      {goals.map((goal, i) => (
        <GoalSection
          defaultOpen
          goal={goal}
           
          key={`${goal.goalContext}-${i}`}
          pdf
          shortTermGoalComponent={CarePlanShortTermGoalSection}
        />
      ))}

      <hr className="h-2 bg-black-200 w-full my-24" />

      <Heading level="h6" className="mb-24">
        {t(`pdfs.carePlan.additionalGoals.title`)}
      </Heading>
      <section className="grid grid-cols-3 gap-24">
        {additionalGoals.map(goal => (
          <React.Fragment key={goal.goalID}>
            <Paragraph className="mb-16 text-[12pt] font-bold">{goal.goalType}</Paragraph>
            <Paragraph className="mb-16 text-[12pt] col-span-2">{goal.detail.goalDescription}</Paragraph>
          </React.Fragment>
        ))}
      </section>

      <hr className="h-2 bg-black-200 w-full my-24" />

      <Paragraph className="mb-16 text-[24pt] font-signature">{fullTherapistName}</Paragraph>
      <Paragraph className="mb-16 text-[10pt] text-black-600">
        {t('pdfs.carePlan.signature.description', {
          name: fullTherapistName,
          date: format(new Date(), `${DAY_MONTH_YEAR} 'at' ${TIME_WITH_ZONE_SHORT}`),
        })}
      </Paragraph>
    </div>
  );
};

const LabelValuePair: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  return (
    <div className="break-inside-avoid">
      <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
        {label}
      </Heading>
      <Paragraph className="mb-16 text-[12pt]">{value}</Paragraph>
    </div>
  );
};
